import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { InfoCircleFilled, CheckCircleFilled } from '@ant-design/icons'
import { passwordSecurityLevel } from '@/config/default'
import intl from 'react-intl-universal'
import { getPasswordRules } from './utils'
import './index.scss'

const ICON_MAP = {
  'info-circle': <InfoCircleFilled />,
  'check-circle': <CheckCircleFilled />
}

const PasswordStrength = ({ passwordLevel, value, min, max, className }) => {
  const options = useMemo(() => {
    return getPasswordRules(passwordLevel, min, max).map(rule => ({
      ...rule,
      iconType: 'info-circle'
    }))
  }, [passwordLevel, min, max])

  // 根据 value 计算校验结果和强度等级
  const { validatedOptions, strengthLevel } = useMemo(() => {
    if (!options.length || !value) {
      return {
        validatedOptions: options,
        strengthLevel: 0
      }
    }

    const validated = options.map(option => {
      const isValid = option.validate(value)

      return {
        ...option,
        iconType: isValid ? 'check-circle' : 'info-circle'
      }
    })

    // 密码强度等级计算
    // 四类正则
    const regLower = /[a-z]/
    const regUpper = /[A-Z]/
    const regNum = /[0-9]/
    // eslint-disable-next-line no-useless-escape
    const regSpecial = /[!@#$%^&*()_+\-=\[\]{}|;:'",.<>/?~\s]/

    // 类型分
    let level = 0
    if (regLower.test(value)) {
      level += 1
    }
    if (regUpper.test(value)) {
      level += 1
    }
    if (regNum.test(value)) {
      level += 1
    }
    if (regSpecial.test(value)) {
      level += 1
    }
    if (level >= 3) {
      level = 3
    }

    return { validatedOptions: validated, strengthLevel: level }
  }, [options, value])

  const strengthLabels = [
    '',
    intl.get('login.passwordStrengthWeak') || '弱',
    intl.get('login.passwordStrengthMedium') || '中',
    intl.get('login.passwordStrengthStrong') || '强'
  ]

  const strengthColors = ['', '#FF4D4F', '#FAAD14', '#52C41A']

  return (
    <div className={`password-strength ${className || ''}`}>
      {/* 强度条 */}
      <div className="password-strength-bar">
        <div className="password-strength-level">
          {[1, 2, 3].map(level => (
            <div
              key={level}
              className={`password-strength-segment ${strengthLevel >= level ? 'active' : ''}`}
              style={{
                backgroundColor: strengthLevel >= level ? strengthColors[strengthLevel] : '#E8E8E8'
              }}
            />
          ))}
        </div>
        {strengthLevel > 0 && (
          <span
            className="password-strength-label"
            style={{ color: strengthColors[strengthLevel] }}
          >
            {strengthLabels[strengthLevel]}
          </span>
        )}
      </div>

      {/* 规则列表 */}
      {validatedOptions.length > 0 && (
        <div className="password-strength-rules">
          {validatedOptions.map(option => (
            <div className="password-strength-rule-item" key={option.id}>
              {ICON_MAP[option.iconType]}
              <span className="password-strength-rule-label">{option.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

PasswordStrength.propTypes = {
  /** 密码安全等级，由企业配置动态设置 */
  passwordLevel: PropTypes.oneOf([
    passwordSecurityLevel.LOW,
    passwordSecurityLevel.MEDIUM,
    passwordSecurityLevel.HIGH
  ]),
  /** 当前密码输入值 */
  value: PropTypes.string,
  /** 密码最小长度（企业自定义，可选） */
  min: PropTypes.number,
  /** 密码最大长度（企业自定义，可选） */
  max: PropTypes.number,
  /** 自定义类名 */
  className: PropTypes.string
}

PasswordStrength.defaultProps = {
  passwordLevel: passwordSecurityLevel.LOW,
  value: '',
  className: ''
}

export default PasswordStrength
